<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue';
import { useI18n } from 'vue-i18n';
import { useElements } from '../../../composables/elements';
import { useStyleClipboard } from '../../../composables/styleClipboard';

const props = defineProps({
  element: { type: Object, required: true },
});

const emit = defineEmits(['pasted']);
const { t } = useI18n();
const { updateElement } = useElements();
const { hasCopied, copyStyle, getCopied } = useStyleClipboard();

const isOpen = ref(false);
const wrapperRef = ref(null);

const onClickOutside = (e) => {
  if (wrapperRef.value && !wrapperRef.value.contains(e.target)) {
    isOpen.value = false;
  }
};

onMounted(() => document.addEventListener('mousedown', onClickOutside));
onBeforeUnmount(() => document.removeEventListener('mousedown', onClickOutside));

const copy = () => {
  copyStyle(props.element.style);
  isOpen.value = false;
};

const paste = async () => {
  if (!hasCopied.value) return;
  await updateElement({ ...props.element, style: getCopied() });
  isOpen.value = false;
  emit('pasted');
};
</script>

<template>
  <div class="style-copy-btn" ref="wrapperRef">
    <div class="btn-icon" @click="isOpen = !isOpen" :title="t('element.styleClipboard.title')">
      <Icon icon="bi:palette" width="32" height="32" />
    </div>

    <div v-if="isOpen" class="sc-dropdown">
      <button class="sc-item" @click="copy">
        <Icon icon="bi:clipboard-plus" width="14" class="me-1" />
        {{ t('element.styleClipboard.copy') }}
      </button>
      <button class="sc-item" @click="paste" :disabled="!hasCopied">
        <Icon icon="bi:clipboard-check" width="14" class="me-1" />
        {{ t('element.styleClipboard.paste') }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.style-copy-btn {
  background: #fff;
  padding: 6px;
  border-radius: 6px;
  border: 2px solid #000;
  cursor: pointer;
  position: relative;
}

.sc-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 170px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 800;
  padding: 4px 0;
}

.sc-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #f2f1f6;
  }

  &:disabled {
    color: #aaa;
    cursor: not-allowed;
  }
}
</style>
